import Checkbox, { ICheckboxChange, IProps as ICheckboxProps } from "@components/inputs/checkbox";
import { Label, MonthCheckboxSelectorWrapper } from "@pages/payroll/Eldp/MonthCSSZSelector.styles";
import i18next from "i18next";
import React, { useCallback, useMemo } from "react";

import TestIds from "../../../testIds";

export enum MasterCheckboxType {
    All = "All",
    Months = "Months",
    Hidden = "Hidden"
}

interface IProps {
    onChange: (value: string) => void;
    value?: string;
    masterCheckboxType?: MasterCheckboxType;
    name?: string;
    isReadOnly?: boolean;
}


const MonthCheckboxSelector: React.FC<IProps> = (props) => {
    const { onChange, value, masterCheckboxType = MasterCheckboxType.Months, name, isReadOnly } = props;

    const labels = useMemo(() => {
        const labels: string[] = [];
        for (let i = 1; i <= 12; i++) {
            labels.push(i.toString());
        }
        switch (masterCheckboxType) {
            case MasterCheckboxType.All:
                labels.unshift(i18next.t("Common:Select.All"));
                break;
            case MasterCheckboxType.Months:
                labels.push("1-12");
                break;
            case MasterCheckboxType.Hidden:
                break;
        }
        return labels;
    }, [masterCheckboxType]);

    const isMonthsMasterCheckbox = masterCheckboxType === MasterCheckboxType.Months;

    const handleChange = useCallback((id: string, checked: boolean) => {
        const dataArrayLength = isMonthsMasterCheckbox ? 13 : 12;
        const newValue = value ? value.split("") : Array(dataArrayLength).fill("0");
        const index = labels.indexOf(id);
        if (index !== -1) {
            newValue[masterCheckboxType === MasterCheckboxType.All ? index - 1 : index] = checked ? "1" : "0";
        }

        if (masterCheckboxType === MasterCheckboxType.All && index === 0) {
            newValue.fill(checked ? "1" : "0");
        }

        onChange(newValue.join(""));
    }, [onChange, value, labels, isMonthsMasterCheckbox, masterCheckboxType]);

    const areMonthsDisabled = value && isMonthsMasterCheckbox && value[12] === "1";
    const isMasterDisabled = value && isMonthsMasterCheckbox && value.slice(0, 12).split("").some(v => v === "1");

    const getValueForIndex = useCallback((index) => {
        if (!value) {
            return false;
        }

        if (masterCheckboxType === MasterCheckboxType.All) {
            if (index === 0) {
                return value.split("").every(v => v === "1");
            }
            return value[index - 1] === "1";
        }

        return value[index] === "1";
    }, [value, masterCheckboxType]);

    return (
        <MonthCheckboxSelectorWrapper
            $hideMasterCheckbox={masterCheckboxType === MasterCheckboxType.Hidden}
            data-testid={TestIds.MonthCheckboxSelector}
            data-name={name}>
                {labels.map(item => (<Label key={item} data-testid={TestIds.CheckboxLabel}>{item}</Label>))}
                {labels.map((item, idx) => (
                        <CheckboxWrapped id={item}
                                         key={`checkbox-${item}`}
                                         onChange={handleChange}
                                         isDisabled={idx < 12 ? areMonthsDisabled : isMasterDisabled}
                                         isReadOnly={isReadOnly}
                                         checked={getValueForIndex(idx)}/>
                ))}
        </MonthCheckboxSelectorWrapper>
    );
};

interface IWrappedCheckboxProps extends Omit<ICheckboxProps, "onChange"> {
    id: string;
    onChange: (id: string, value: boolean) => void;
}

const CheckboxWrapped: React.FC<IWrappedCheckboxProps> = (({ onChange, id, ...restProps }) => {
    const handleChange = useCallback((args: ICheckboxChange) => {
        onChange(id, args.value);
    }, [onChange, id]);

    return (
            <Checkbox onChange={handleChange} {...restProps} />
    );
});

export default MonthCheckboxSelector;